import type { MetricSeries } from '@bartizo/shared';
import { useQuery } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { getMetricSeries } from '../api/metrics.js';
import { formatBytes, formatRate } from '../lib/format.js';
import { Alert } from '../ui/Alert.js';
import { Card } from '../ui/Card.js';
import { SelectField } from '../ui/Field.js';
import { MetricChart } from './MetricChart.js';

/**
 * Databases, on a server's page (knowledge doc section 8.4).
 *
 * The same two questions as `DiskCard`, asked of databases: **what is growing** is
 * a line per database with its rate beside it, and **what is taking the room** is
 * a ranked list of tables, because twenty lines on one chart answer nothing.
 *
 * Only drawn for a server that has reported a database size. Most servers run no
 * database, and an empty card on every web server's page would teach people to
 * scroll past the one that matters. A server that has the database role and no
 * targets says so in its module health, which is where the reason belongs.
 *
 * Nothing here knows which engine produced a line: section 6's promise is that
 * every engine arrives as the same metrics, so this reads labels and nothing else.
 * The one label that changes the drawing is `medium`. An instance whose size is
 * memory — Redis and Valkey, which keep no size per database — is charted as
 * memory, on its own axis, because a gigabyte of RAM and a gigabyte of disk run out
 * for different reasons. An instance on disk already has its databases charted.
 */

const RANGES = [
  { value: '24', label: 'Last day' },
  { value: '168', label: 'Last week' },
  { value: '720', label: 'Last 30 days' },
] as const;

/** Section 8.4 measures every half hour, so a week is the first range with a shape. */
const DEFAULT_RANGE = '168';

export function DatabaseCard({
  serverId,
  timezone,
}: {
  serverId: string;
  timezone: string;
}): ReactNode {
  const [hours, setHours] = useState<string>(DEFAULT_RANGE);

  const since = (): string => new Date(Date.now() - Number(hours) * 3_600_000).toISOString();

  const sizes = useQuery({
    queryKey: ['metrics', serverId, 'db_size_bytes', hours],
    queryFn: () => getMetricSeries({ serverId, name: 'db_size_bytes', from: since() }),
  });

  const objects = useQuery({
    queryKey: ['metrics', serverId, 'db_object_size_bytes', hours],
    queryFn: () => getMetricSeries({ serverId, name: 'db_object_size_bytes', from: since() }),
  });

  const instanceSizes = useQuery({
    queryKey: ['metrics', serverId, 'db_instance_size_bytes', hours],
    queryFn: () => getMetricSeries({ serverId, name: 'db_instance_size_bytes', from: since() }),
  });

  const memory = (instanceSizes.data?.series ?? []).filter(
    (line) => line.labels['medium'] === 'memory',
  );

  // Nothing to say until there is something, and nothing at all for a server
  // with no databases.
  if (sizes.isPending || instanceSizes.isPending) return null;
  if (
    sizes.data?.series.length === 0 &&
    (objects.data?.series.length ?? 0) === 0 &&
    memory.length === 0
  ) {
    return null;
  }

  const series = sizes.data?.series ?? [];
  const instances = new Set(series.map((line) => line.labels['instance']));
  const label = (line: MetricSeries): string => {
    const database = line.labels['database'] ?? 'unknown database';
    // The instance is only worth naming when there is more than one to tell apart.
    return instances.size > 1 ? `${database} on ${line.labels['instance'] ?? '?'}` : database;
  };

  return (
    <Card
      title="Databases"
      description="Size per database, memory for in-memory stores, and the largest tables and collections."
      aside={
        <SelectField
          label="Range"
          value={hours}
          onChange={(event) => {
            setHours(event.target.value);
          }}
        >
          {RANGES.map((range) => (
            <option key={range.value} value={range.value}>
              {range.label}
            </option>
          ))}
        </SelectField>
      }
    >
      {sizes.isError && (
        <Alert tone="danger" title="Could not load the database sizes">
          Try again in a moment.
        </Alert>
      )}

      {sizes.data !== undefined && series.length > 0 && (
        <>
          <MetricChart
            title="Size, per database"
            series={series}
            from={sizes.data.from}
            to={sizes.data.to}
            bucketSeconds={sizes.data.bucketSeconds}
            format={formatBytes}
            seriesLabel={label}
            yMin={0}
            timezone={timezone}
          />
          <Growth series={series} label={label} />
        </>
      )}

      {instanceSizes.data !== undefined && memory.length > 0 && (
        <>
          <MetricChart
            title="Memory, per instance"
            series={memory}
            from={instanceSizes.data.from}
            to={instanceSizes.data.to}
            bucketSeconds={instanceSizes.data.bucketSeconds}
            format={formatBytes}
            seriesLabel={(line) => line.labels['instance'] ?? 'unknown instance'}
            yMin={0}
            timezone={timezone}
          />
          <Growth
            series={memory}
            label={(line) => `${line.labels['instance'] ?? 'unknown instance'} (memory)`}
            quiet
          />
        </>
      )}

      {objects.data !== undefined && objects.data.series.length > 0 && (
        <LargestTables series={objects.data.series} showInstance={instances.size > 1} />
      )}
    </Card>
  );
}

/**
 * The databases that are actually getting bigger, fastest first. A database has
 * no "full" the way a disk does, so this is a rate rather than a forecast; the
 * disk card beside it is where "full in N days" lives.
 */
function Growth({
  series,
  label,
  quiet = false,
}: {
  series: MetricSeries[];
  label: (line: MetricSeries) => string;
  /** Say nothing rather than "nothing is growing", where the chart above says it already. */
  quiet?: boolean;
}): ReactNode {
  const growing = series
    .filter((line) => (line.trend?.changePerDay ?? 0) > 0)
    .sort((a, b) => (b.trend?.changePerDay ?? 0) - (a.trend?.changePerDay ?? 0))
    .slice(0, 5);

  if (growing.length === 0) {
    if (quiet) return null;
    return (
      <p className="muted text-sm">
        No database is growing over this range. The rate needs at least three readings.
      </p>
    );
  }

  return (
    <ul className="forecasts">
      {growing.map((line) => (
        <li key={`${line.labels['instance'] ?? ''}:${line.labels['database'] ?? ''}`}>
          <strong>{label(line)}</strong> is growing
          <span className="cell-sub">
            {formatRate(line.trend?.changePerDay ?? 0, formatBytes)}, fitted over{' '}
            {String(line.trend?.samples ?? 0)} readings
          </span>
        </li>
      ))}
    </ul>
  );
}

/**
 * Section 8.4's twenty largest, of which the ten biggest are shown. Tables for the
 * SQL engines, collections for MongoDB; Redis has none, since its only way to find
 * big keys reads key names, which are the application's data.
 */
function LargestTables({
  series,
  showInstance,
}: {
  series: MetricSeries[];
  showInstance: boolean;
}): ReactNode {
  const latest = series
    .map((line) => ({
      instance: line.labels['instance'] ?? '',
      database: line.labels['database'] ?? '',
      object: line.labels['object'] ?? 'unknown',
      bytes: line.points[line.points.length - 1]?.avg ?? 0,
    }))
    .sort((a, b) => b.bytes - a.bytes)
    .slice(0, 10);

  const largest = latest[0]?.bytes ?? 1;

  return (
    <>
      <h3 className="subhead">Largest tables and collections</h3>
      <p className="muted text-xs">
        Including their indexes. Read from the database’s own statistics, never from its rows.
      </p>
      <table className="table folder-table">
        <caption className="visually-hidden">
          The ten largest tables and collections, by size
        </caption>
        <thead>
          <tr>
            <th scope="col">Table or collection</th>
            <th scope="col">Size</th>
          </tr>
        </thead>
        <tbody>
          {latest.map((table) => (
            <tr key={`${table.instance}:${table.database}:${table.object}`}>
              <th scope="row">
                <code>{table.object}</code>
                <span className="cell-sub">
                  in {table.database}
                  {showInstance ? ` on ${table.instance}` : ''}
                </span>
              </th>
              <td>
                <span className="bar-cell">
                  <span
                    className="bar"
                    style={{ width: `${String(Math.max(2, (table.bytes / largest) * 100))}%` }}
                    aria-hidden="true"
                  />
                  <span className="bar-value">{formatBytes(table.bytes)}</span>
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}
